<script setup lang="ts">
  import { computed } from 'vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Switch } from '@/components/ui/switch'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import type { MinimalOrganisation } from '@/types/generated'

  const { t } = useTranslation({
    en: {
      customers: 'Customers',
      visibleToCustomers: 'Visible to customers',
      allCustomers: 'All customers',
      allCustomersDescription:
        'Every customer will have access to this questionnaire, including any customers added in the future',
      selectedCustomer: 'Selected customer',
      selectedCustomerDescription: 'Select which customer will have access to this questionnaire',
      assignedOrganization: 'Assigned Organization',
      noOrganization: 'None',
      loadingOrganizations: 'Loading organizations...'
    },
    de: {
      customers: 'Kunden',
      visibleToCustomers: 'Sichtbar für Kunden',
      allCustomers: 'Alle Kunden',
      allCustomersDescription:
        'Jeder Kunde hat Zugriff auf diesen Fragebogen, einschließlich aller zukünftig hinzugefügten Kunden',
      selectedCustomer: 'Ausgewählter Kunde',
      selectedCustomerDescription:
        'Wählen Sie aus, welcher Kunde Zugriff auf diesen Fragebogen hat',
      assignedOrganization: 'Zugewiesene Organisation',
      noOrganization: 'Keine',
      loadingOrganizations: 'Organisationen werden geladen...'
    }
  })

  const props = withDefaults(
    defineProps<{
      visibleToCustomers: boolean
      allCustomersSelected: boolean
      selectedOrg: string | null
      organisations?: MinimalOrganisation[]
      totalCount?: number
      isLoading?: boolean
    }>(),
    {
      organisations: () => [],
      totalCount: 0,
      isLoading: false
    }
  )

  const emit = defineEmits<{
    update: [data: { visible: boolean; allSelected: boolean; selected: string | null }]
  }>()

  const organizationOptions = computed<Option[]>(() => {
    if (!props.organisations?.length) return []
    return props.organisations.map((org) => ({
      value: org.uuid,
      title: org.name || ''
    }))
  })

  const handleVisibilityChange = (value: boolean) => {
    emit('update', {
      visible: value,
      allSelected: props.allCustomersSelected,
      selected: props.selectedOrg
    })
  }

  const handleAllCustomersChange = () => {
    emit('update', {
      visible: props.visibleToCustomers,
      allSelected: true,
      selected: null
    })
  }

  const handleSelectedCustomerChange = () => {
    emit('update', {
      visible: props.visibleToCustomers,
      allSelected: false,
      selected: props.selectedOrg
    })
  }

  const handleOrgChange = (value: string | null) => {
    emit('update', {
      visible: props.visibleToCustomers,
      allSelected: false,
      selected: value
    })
  }
</script>

<template>
  <div class="rounded-lg border border-gray-200 bg-white">
    <!-- Header -->
    <div class="flex items-center justify-between border-b border-gray-200 p-6">
      <h2 class="text-lg font-semibold text-gray-900">{{ t('customers') }}</h2>
      <div class="flex items-center gap-3">
        <span class="text-sm text-gray-600">{{ t('visibleToCustomers') }}</span>
        <Switch
          :checked="visibleToCustomers"
          @update:checked="handleVisibilityChange"
        />
      </div>
    </div>

    <!-- Content -->
    <div
      v-if="visibleToCustomers"
      class="p-6"
    >
      <!-- Radio Options -->
      <div
        class="space-y-4"
        role="radiogroup"
        :aria-label="t('customers')"
      >
        <!-- All customers option -->
        <label class="flex cursor-pointer items-start gap-3">
          <div class="mt-0.5">
            <input
              type="radio"
              name="customer-scope"
              value="all"
              :checked="allCustomersSelected"
              class="h-5 w-5 cursor-pointer border-2 border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500
                focus:ring-offset-1"
              @change="handleAllCustomersChange"
            />
          </div>
          <div class="flex-1">
            <p class="font-medium text-gray-900">{{ t('allCustomers') }}</p>
            <p class="text-sm text-gray-500">{{ t('allCustomersDescription') }}</p>
          </div>
        </label>

        <!-- Selected customer option -->
        <label class="flex cursor-pointer items-start gap-3">
          <div class="mt-0.5">
            <input
              type="radio"
              name="customer-scope"
              value="selected"
              :checked="!allCustomersSelected"
              class="h-5 w-5 cursor-pointer border-2 border-gray-300 text-purple-600 focus:ring-2 focus:ring-purple-500
                focus:ring-offset-1"
              @change="handleSelectedCustomerChange"
            />
          </div>
          <div class="flex-1">
            <p class="font-medium text-gray-900">{{ t('selectedCustomer') }}</p>
            <p class="text-sm text-gray-500">{{ t('selectedCustomerDescription') }}</p>
          </div>
        </label>
      </div>

      <!-- Organization Dropdown (only shown when "Selected customer" is chosen) -->
      <div
        v-if="!allCustomersSelected"
        class="mt-6"
      >
        <!-- Loading state -->
        <div
          v-if="isLoading"
          class="flex items-center gap-2 text-sm text-gray-500"
        >
          <div
            class="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-purple-600"
          />
          <span>{{ t('loadingOrganizations') }}</span>
        </div>

        <SelectField
          v-else
          :model-value="selectedOrg"
          :label="t('assignedOrganization')"
          name="org"
          :options="organizationOptions"
          :is-filterable="true"
          :allow-unselect="true"
          :unselected-option-text="t('noOrganization')"
          @update:model-value="handleOrgChange"
        />
      </div>
    </div>
  </div>
</template>
